<template>
    <div class="code-editor-wrapper">
        <label v-if="label" class="code-editor-label">
            {{ label }}
            <span v-if="required" class="error--text">*</span>
        </label>
        
        <div 
            class="code-editor-container" 
            :class="{ 'code-editor-container--outlined': outlined, 'code-editor-container--dense': dense }"
            :style="{ height: containerHeight + 'px' }"
        >
            <div class="code-editor-content">
                <pre class="code-highlight"><code ref="highlightedCode" class="language-sql">{{ value || ' ' }}</code></pre>
                <textarea
                    v-model="internalValue"
                    class="code-input"
                    :placeholder="placeholder"
                    :readonly="readonly"
                    @input="handleInput"
                    @scroll="syncScroll"
                    spellcheck="false"
                ></textarea>
            </div>
        </div>
        
        <div v-if="hint && persistentHint" class="code-editor-hint">
            {{ hint }}
        </div>
    </div>
</template>

<script>
import hljs from 'highlight.js/lib/core'
import sql from 'highlight.js/lib/languages/sql'

// Registra il linguaggio SQL
hljs.registerLanguage('sql', sql)

export default {
    name: 'CodeEditor',
    props: {
        value: {
            type: String,
            default: ''
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        hint: {
            type: String,
            default: ''
        },
        persistentHint: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: true
        },
        dense: {
            type: Boolean,
            default: false
        },
        rows: {
            type: [Number, String],
            default: 5
        },
        required: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        language: {
            type: String,
            default: 'sql'
        }
    },
    data() {
        return {
            internalValue: this.value || '',
            updateTimeout: null,
            containerHeight: 200
        }
    },
    watch: {
        value(newVal) {
            if (newVal !== this.internalValue) {
                this.internalValue = newVal || ''
                this.$nextTick(() => {
                    this.updateHighlight()
                })
            }
        }
    },
    mounted() {
        // Calcola l'altezza iniziale basata sul numero di righe
        const lineHeight = 21 // 14px font * 1.5 line-height
        const padding = 32 // 16px top + 16px bottom
        const minRows = parseInt(this.rows) || 5
        this.containerHeight = Math.max(200, (minRows * lineHeight) + padding)
        
        this.updateHighlight()
    },
    methods: {
        handleInput(event) {
            this.internalValue = event.target.value
            this.$emit('input', this.internalValue)
            
            // Minimal debounce for performance but still responsive
            this.updateHighlight()
            // clearTimeout(this.updateTimeout)
            // this.updateTimeout = setTimeout(() => {
            // }, 10) // Reduced from 100ms to 10ms
        },
        
        updateHighlight() {
            if (this.$refs.highlightedCode) {
                // Reset the highlighted state before updating
                delete this.$refs.highlightedCode.dataset.highlighted
                this.$refs.highlightedCode.textContent = this.internalValue || ' '
                hljs.highlightElement(this.$refs.highlightedCode)
            }
        },
        
        syncScroll(event) {
            const textarea = event.target
            const pre = this.$el.querySelector('.code-highlight')
            if (pre) {
                pre.scrollTop = textarea.scrollTop
                pre.scrollLeft = textarea.scrollLeft
            }
        }
    }
}
</script>

<style lang="scss">
// Don't import highlight.js theme - we'll handle colors ourselves
// @import '~highlight.js/styles/atom-one-dark.css';

.code-editor-wrapper {
    margin-bottom: 20px;
}

.code-editor-label {
    display: block;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 8px;
    font-weight: 400;
}

.code-editor-container {
    position: relative;
    background: #282c34;
    border-radius: 4px;
    overflow: hidden;
    resize: vertical;
    min-height: 100px;
    
    &--outlined {
        border: 1px solid rgba(0, 0, 0, 0.23);
    }
    
    &--dense {
        .code-editor-content {
            min-height: 100px;
        }
    }
}

.code-editor-content {
    position: relative;
    min-height: 150px;
    height: 100%;
}

.code-highlight,
.code-input {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 16px;
    border: 0;
    overflow: auto;
    resize: none;
    outline: none;
    
    // CRITICAL: Exact same font settings for perfect alignment
    font-family: monospace;
    font-size: 13px;
    line-height: 1.5;
    font-weight: 400;
    letter-spacing: normal;
    font-variant-ligatures: none;
    
    // Text rendering - must be identical
    white-space: pre;
    word-wrap: normal;
    word-break: normal;
    word-spacing: normal;
    tab-size: 4;
    -webkit-font-smoothing: auto;
    -moz-osx-font-smoothing: auto;
    
    // Box model
    box-sizing: border-box;
}

.code-highlight {
    pointer-events: none;
    z-index: 1;
    color: #abb2bf;
    
    code {
        background: transparent;
        padding: 0;
        margin: 0;
        border: 0;
        display: block;
        
        // MUST match textarea font exactly
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;
        letter-spacing: inherit;
        font-variant-ligatures: inherit;
        
        // Override all possible highlight.js styles
        * {
            font: inherit !important;
            font-family: inherit !important;
            font-size: inherit !important;
            line-height: inherit !important;
            font-weight: inherit !important;
            font-style: inherit !important;
            letter-spacing: inherit !important;
            text-transform: none !important;
            vertical-align: baseline !important;
        }
        
        // SQL keyword colors - only change color, nothing else
        .hljs-keyword {
            color: #c678dd;
        }
        
        .hljs-string {
            color: #98c379;
        }
        
        .hljs-number {
            color: #d19a66;
        }
        
        .hljs-operator {
            color: #56b6c2;
        }
        
        .hljs-comment {
            color: #5c6370;
        }
        
        .hljs-built_in {
            color: #e06c75;
        }
    }
}

.code-input {
    background: transparent;
    color: transparent;
    caret-color: #528bff;
    z-index: 2;
    
    &::selection {
        background: rgba(82, 139, 255, 0.25);
    }
    
    &::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.1);
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 5px;
        
        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
}

.code-editor-hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 4px;
}

// Light theme support
.theme--light {
    .code-editor-container {
        background: #fafafa;
        border-color: rgba(0, 0, 0, 0.23);
    }
    
    .code-highlight {
        color: #383a42;
        
        code {
            // Ensure font consistency in light theme too
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
            font-weight: inherit;
            
            .hljs-keyword {
                color: #a626a4;
            }
            
            .hljs-string {
                color: #50a14f;
            }
            
            .hljs-number {
                color: #986801;
            }
            
            .hljs-operator {
                color: #0184bb;
            }
            
            .hljs-comment {
                color: #a0a1a7;
            }
            
            .hljs-built_in {
                color: #e45649;
            }
        }
    }
    
    .code-input {
        caret-color: #4078f2;
        
        &::selection {
            background: rgba(64, 120, 242, 0.15);
        }
    }
}
</style>